import { hasComparablePriceHistory } from "@/lib/has-comparable-price-history";
import { hasTrustedPriceDrop } from "@/lib/has-trusted-price-drop";
import { offerPriceAssessment } from "@/lib/offer-price-assessment";
import type { Credit } from "@garimpa/lib/musicTypes";
import {
  displayDealPriceIndicators,
  type DealPriceIndicator,
} from "@garimpa/lib/notificationEmails";

type CompanyFaqSource = {
  companyId: string | null;
  companyName: string;
  role: string;
};

const MAX_FAQS_PER_CATEGORY = 5;

export const CREDIT_FAQ_FAMILIES = [
  { key: "producer", primaryRoles: ["producer"], secondaryRoles: ["co_producer"] },
  { key: "soundEngineer", primaryRoles: ["sound_engineer"] },
  { key: "engineer", primaryRoles: ["engineer"] },
  { key: "recording", primaryRoles: ["recording"] },
  {
    key: "mastering",
    primaryRoles: ["mastering"],
    secondaryRoles: ["mastered_by_assistant"],
  },
  { key: "mixing", primaryRoles: ["mix"], secondaryRoles: ["assistant_mix"] },
  { key: "composer", primaryRoles: ["composer"] },
  { key: "writer", primaryRoles: ["writer"] },
  { key: "lyricist", primaryRoles: ["lyricist"] },
  { key: "arranger", primaryRoles: ["arranger"], secondaryRoles: ["orchestrator"] },
  { key: "vocals", primaryRoles: ["lead_vocals", "vocals", "vocal"] },
  {
    key: "guitar",
    primaryRoles: ["guitar", "lead_guitar", "rhythm_guitar", "electric_guitar", "acoustic_guitar"],
  },
  { key: "bass", primaryRoles: ["bass", "bass_guitar", "electric_bass_guitar"] },
  { key: "drums", primaryRoles: ["drums", "drums_drum_set", "percussion"] },
] as const;

export type CreditFaqFamilyKey = (typeof CREDIT_FAQ_FAMILIES)[number]["key"];

export const COMPANY_FAQ_ROLES = [
  "recorded_at",
  "pressed_by",
  "manufactured_by",
  "made_by",
  "mastered_at",
  "remastered_at",
  "mixed_at",
  "lacquer_cut_at",
  "glass_mastered_at",
  "distributed_by",
  "published_by",
  "record_company",
  "phonographic_copyright_p",
  "copyright_c",
] as const;

export type CompanyFaqRole = (typeof COMPANY_FAQ_ROLES)[number];

const WORK_COMPANY_FAQ_ROLES = ["recorded_at"] as const satisfies readonly CompanyFaqRole[];

export type CreditFaqSelection = {
  key: CreditFaqFamilyKey;
  primary: Credit[];
  secondary: Credit[];
};

export type CompanyFaqSelection = {
  key: CompanyFaqRole;
  companies: CompanyFaqSource[];
};

function uniqueCredits(credits: Credit[]): Credit[] {
  const seen = new Set<string>();
  return credits.filter((credit) => {
    const key = credit.artistId || credit.artistName;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function creditsForRoles(creditsByRole: Record<string, Credit[]>, roles: readonly string[]) {
  return uniqueCredits(roles.flatMap((role) => creditsByRole[role] ?? []));
}

export function selectCreditFaqs(
  creditsByRole: Record<string, Credit[]> | null | undefined,
): CreditFaqSelection[] {
  const roleMap = creditsByRole ?? {};
  return CREDIT_FAQ_FAMILIES.map((family) => ({
    key: family.key,
    primary: creditsForRoles(roleMap, family.primaryRoles),
    secondary: creditsForRoles(roleMap, "secondaryRoles" in family ? family.secondaryRoles : []),
  }))
    .filter((family) => family.primary.length > 0 || family.secondary.length > 0)
    .slice(0, MAX_FAQS_PER_CATEGORY);
}

export function selectCompanyFaqs(
  companies: CompanyFaqSource[] | null | undefined,
): CompanyFaqSelection[] {
  const byRole = new Map<string, CompanyFaqSource[]>();
  const seen = new Set<string>();

  for (const company of companies ?? []) {
    const key = `${company.role}:${company.companyId ?? ""}:${company.companyName}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const group = byRole.get(company.role);
    if (group) group.push(company);
    else byRole.set(company.role, [company]);
  }

  return COMPANY_FAQ_ROLES.flatMap((role) => {
    const matches = byRole.get(role);
    return matches?.length ? [{ key: role, companies: matches }] : [];
  }).slice(0, MAX_FAQS_PER_CATEGORY);
}

export function selectWorkCompanyFaqs(
  companies: CompanyFaqSource[] | null | undefined,
): CompanyFaqSelection[] {
  const allowed = new Set<CompanyFaqRole>(WORK_COMPANY_FAQ_ROLES);
  return selectCompanyFaqs(companies).filter((faq) => allowed.has(faq.key));
}

export type PriceAssessment =
  | "insufficient_history"
  | "expensive"
  | "expensive_with_drop"
  | "all_time_low"
  | "all_time_low_with_drop"
  | "below_typical"
  | "below_typical_with_drop"
  | "fair_price";

export type PricingFaqSummary = {
  bestOfferPrice: string;
  currency: string;
  dealConfidence: string;
  dealDeltaPercent: string | null;
  dealMovement: string;
  dealReferencePrice: string | null;
  priceHistoryAverage: string | null;
  priceHistoryCurrency: string | null;
  priceHistoryObservationCount: number;
  priceHistoryTypical: string | null;
  priceIndicators: string[];
};

export type PriceAssessmentResult = {
  assessment: PriceAssessment;
  comparisonIndicator: DealPriceIndicator | null;
  hasTrustedDrop: boolean;
};

export function assessOfferPrice(summary: PricingFaqSummary): PriceAssessmentResult {
  const comparisonIndicator = displayDealPriceIndicators(summary.priceIndicators)[0] ?? null;
  return offerPriceAssessment(
    hasComparablePriceHistory(summary),
    comparisonIndicator,
    hasTrustedPriceDrop(summary),
  );
}
